import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import RegistroPage from './pages/RegistroPage';
import RecuperarSenhaPage from './pages/RecuperarSenhaPage';
import Dashboard from './pages/Dashboard';
import MenuPublico from './pages/MenuPublico';
import MonitorPedidos from './pages/MonitorPedidos';
import GarcomLoginPage from './pages/GarcomLoginPage';
import GarcomHomePage from './pages/GarcomHomePage';
import GarcomMesaPage from './pages/GarcomMesaPage';
import GarcomComandaPage from './pages/GarcomComandaPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GarcomAuthProvider, useGarcomAuth } from './contexts/GarcomAuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';
import './styles/mobile-optimizations.css';

import GarcomErrorBoundary from './components/GarcomErrorBoundary';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { usuario, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }
  
  return usuario ? <>{children}</> : <Navigate to="/login" />;
};

const GarcomPrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { garcom, isLoading } = useGarcomAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }
  
  return (
    <GarcomErrorBoundary>
      {garcom ? <>{children}</> : <Navigate to="/garcom/login" />}
    </GarcomErrorBoundary>
  );
};

const App: React.FC = () => {
  // Liberar localStorage para o app Android (WebView)
  React.useEffect(() => {
    if (window.AndroidAuth) {
      try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        console.log('✅ localStorage liberado para o app Android');
      } catch (e) {
        console.log('⚠️ localStorage ainda bloqueado');
      }
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <GarcomAuthProvider>
          <Router>
          <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/login/:empresaId" element={<LoginPage />} />
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/registro" element={<RegistroPage />} />
              <Route path="/registro/:empresaId" element={<RegistroPage />} />
              <Route path="/recuperar-senha" element={<RecuperarSenhaPage />} />
              <Route path="/recuperar-senha/:empresaId" element={<RecuperarSenhaPage />} />
              <Route path="/dashboard/*" element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              } />
              <Route path="/menu/:empresaId" element={<MenuPublico />} />
              <Route path="/monitor" element={<MonitorPedidos />} />
              
              {/* Rotas do Garçom */}
              <Route path="/garcom/login" element={<GarcomLoginPage />} />
              <Route path="/garcom/home" element={
                <GarcomPrivateRoute>
                  <GarcomHomePage />
                </GarcomPrivateRoute>
              } />
              <Route path="/garcom/dashboard" element={<Navigate to="/garcom/home" replace />} />
              <Route path="/garcom/mesa/:id" element={
                <GarcomPrivateRoute>
                  <GarcomMesaPage />
                </GarcomPrivateRoute>
              } />
              <Route path="/garcom/comanda/:comandaId" element={
                <GarcomPrivateRoute>
                  <GarcomComandaPage />
                </GarcomPrivateRoute>
              } />
              
              <Route path="/politica-privacidade" element={<PrivacyPolicyPage />} />
              <Route path="/politica-privacidade/:empresaId" element={<PrivacyPolicyPage />} />
              
              <Route path="/" element={<Navigate to="/menu/1" />} />
            </Routes>
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#1f2937',
                  color: '#fff',
                  border: '1px solid #374151'
                },
                success: {
                  style: {
                    background: '#059669',
                    color: '#fff'
                  }
                },
                error: {
                  style: {
                    background: '#dc2626',
                    color: '#fff'
                  }
                }
              }}
            />
          </div>
        </Router>
      </GarcomAuthProvider>
    </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
